import { FieldTypeOption } from "@kaltura/mediaspace-shared-types";
import { Schedule } from "./schedule/Schedule";
import { Container, StyledFormLabel } from "./CommonStyles";
import styled from "@emotion/styled";
import { Typography } from "@kaltura/ds-react-components";
import { useId } from "react";

export interface ScheduleFieldProps {
    /**
     * Main label for the scheduling section
     */
    label: string;

    /**
     * Optional description text explaining the scheduling options
     */
    description?: string;

    /**
     * List of available timezones
     */
    timezones: FieldTypeOption[];

    /**
     * Field prefix for Schedule component fields (startTimeDate, startTimeTime, endTimeDate, endTimeTime, tz)
     */
    fieldPrefix?: string;

    /**
     * Whether to show description in a tooltip or under the title
     * @default true
     */
    descriptionTooltip?: boolean;

    /**
     * Whether to make the label text bold
     * @default false
     */
    fontWeightBold?: boolean;

    /**
     * if true, inputs are disabled
     */
    disabled?: boolean;

    inline?: boolean;
}

const StyledDescription = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone2,
    marginBottom: theme.spacing(1),
}));

/**
 * ScheduleField component provides date/time/timezone inputs without conditional logic.
 * Displays schedule inputs directly without radio button selection.
 */
export const ScheduleField = ({
    label,
    description,
    timezones,
    fieldPrefix,
    descriptionTooltip = true,
    fontWeightBold = false,
    disabled = false,
    inline = false,
}: ScheduleFieldProps) => {
    const randomId = useId();
    const labelId = fieldPrefix ? `${fieldPrefix}-id` : `${randomId}-id`;

    return (
        <Container inline={inline}>
            <StyledFormLabel
                id={labelId}
                text={label}
                description={descriptionTooltip ? description : undefined}
                fontWeightBold={fontWeightBold}
            />
            {!descriptionTooltip && description && <StyledDescription variant="body2">{description}</StyledDescription>}
            <Schedule timezones={timezones} fieldPrefix={fieldPrefix} disabled={disabled} aria-describedby={labelId} />
        </Container>
    );
};

export default ScheduleField;
